import React, { useCallback, useEffect, useState } from 'react';
import { Download, Search, X, SprayCan } from 'lucide-react';
import api from '../../lib/api';
import { Sheet, SheetContent } from '../../components/ui/sheet';

const short = (s) => (!s ? '—' : `${Math.floor(s / 60)}m${String(Math.floor(s % 60)).padStart(2, '0')}`);
const hhmm = (s) => {
  const v = Math.max(0, Math.floor(s || 0));
  return `${Math.floor(v / 3600)}h${String(Math.floor((v % 3600) / 60)).padStart(2, '0')}`;
};
const dtFmt = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};
const iso = (d) => d.toISOString().slice(0, 10);

const Row = ({ label, value }) => (
  <div className="flex items-start justify-between gap-3 border-b border-border px-3 py-2">
    <span className="ent-label">{label}</span>
    <span className="text-right text-xs font-semibold text-foreground">{value || '—'}</span>
  </div>
);

export const ExecutedJobs = ({ focusId = '' }) => {
  const today = new Date();
  const [start, setStart] = useState(focusId ? '2020-01-01' : iso(new Date(today.getTime() - 6 * 86400000)));
  const [end, setEnd] = useState(iso(today));
  const [worker, setWorker] = useState('');
  const [tipo, setTipo] = useState('');
  const [q, setQ] = useState('');
  const [data, setData] = useState({ jobs: [], totals: {}, workers: [] });
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ start, end });
      if (worker) p.set('worker_id', worker);
      if (tipo) p.set('tipo', tipo);
      if (q.trim()) p.set('q', q.trim());
      const { data: d } = await api.get(`/detail/jobs?${p.toString()}`);
      setData(d);
    } catch (e) {} finally { setLoading(false); }
  }, [start, end, worker, tipo, q]);

  useEffect(() => { const t = setTimeout(load, q ? 400 : 0); return () => clearTimeout(t); }, [load, q]);

  useEffect(() => {
    if (!focusId) return;
    const j = (data.jobs || []).find((x) => x.id === focusId);
    if (j) setDetail(j);
  }, [focusId, data.jobs]);

  const exportCsv = () => {
    const head = ['Finalizado em', 'VIN', 'Ano', 'Marca', 'Modelo', 'Trim', 'Cor', 'Tipo', 'Profissional', 'Duracao (min)', 'Notas'];
    const lines = (data.jobs || []).map((j) => [
      dtFmt(j.finished_at), j.vin, j.ano, j.marca, j.modelo, j.trim, j.cor, j.tipo_label,
      j.worker_name, Math.round((j.duration_seconds || 0) / 60), (j.notas || '').replace(/[\n;]/g, ' '),
    ]);
    const csv = [head, ...lines].map((r) => r.map((c) => `"${String(c ?? '')}"`).join(';')).join('\n');
    const url = URL.createObjectURL(new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url; a.download = `detail-executados-${start}_${end}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const t = data.totals || {};

  return (
    <div className="space-y-4" data-testid="detail-executed">
      <div className="ent-panel ent-ribbon" data-testid="detail-executed-metrics">
        <div className="px-4 py-3"><p className="ent-label">Carros executados</p><p className="dt-timer mt-1 text-2xl font-black leading-none">{t.count ?? 0}</p><p className="mt-1 text-[11px] text-muted-foreground/70">no período filtrado</p></div>
        <div className="px-4 py-3"><p className="ent-label">Tempo total</p><p className="dt-timer mt-1 text-2xl font-black leading-none">{hhmm(t.total_seconds)}</p><p className="mt-1 text-[11px] text-muted-foreground/70">soma das execuções</p></div>
        <div className="px-4 py-3"><p className="ent-label">Tempo médio</p><p className="dt-timer mt-1 text-2xl font-black leading-none">{short(t.avg_seconds)}</p><p className="mt-1 text-[11px] text-muted-foreground/70">por veículo</p></div>
        <div className="px-4 py-3"><p className="ent-label">Delivery / Entrada</p><p className="dt-timer mt-1 text-2xl font-black leading-none">{t.delivery ?? 0} / {t.entrada ?? 0}</p><p className="mt-1 text-[11px] text-muted-foreground/70">distribuição por tipo</p></div>
      </div>

      <div className="ent-panel">
        <div className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-2.5">
          <span className="ent-label mr-1">Filtros</span>
          <input data-testid="dx-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} className="ent-input" style={{ width: 142 }} />
          <span className="text-xs text-muted-foreground">–</span>
          <input data-testid="dx-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="ent-input" style={{ width: 142 }} />
          <select data-testid="dx-worker" value={worker} onChange={(e) => setWorker(e.target.value)} className="ent-input" style={{ width: 180 }}>
            <option value="">Todos os profissionais</option>
            {(data.workers || []).map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
          <select data-testid="dx-tipo" value={tipo} onChange={(e) => setTipo(e.target.value)} className="ent-input" style={{ width: 160 }}>
            <option value="">Todos os tipos</option>
            <option value="delivery">Delivery</option>
            <option value="entrada">Detail Entrada</option>
          </select>
          <div className="relative" style={{ width: 200 }}>
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground" />
            <input data-testid="dx-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="VIN, modelo, cor…" className="ent-input" style={{ paddingLeft: 26 }} />
          </div>
          <button data-testid="dx-export" onClick={exportCsv} disabled={!data.jobs?.length} className="ent-btn ent-btn-sm ml-auto h-8 px-3">
            <Download className="h-3 w-3" /> CSV
          </button>
        </div>

        {loading && !data.jobs?.length ? (
          <p className="ent-empty">Carregando…</p>
        ) : !data.jobs?.length ? (
          <div className="p-10 text-center">
            <SprayCan className="mx-auto h-8 w-8 text-muted-foreground/40" />
            <p className="mt-3 text-sm text-muted-foreground">Nenhum carro executado no período selecionado.</p>
          </div>
        ) : (
          <div className="max-h-[560px] overflow-y-auto">
            <table className="ent-table">
              <thead><tr><th>Finalizado</th><th>Veículo</th><th>VIN</th><th>Cor</th><th>Tipo</th><th>Profissional</th><th className="text-right">Duração</th></tr></thead>
              <tbody>
                {data.jobs.map((j) => (
                  <tr key={j.id} onClick={() => setDetail(j)} className="cursor-pointer" data-testid={`dx-row-${j.id}`}>
                    <td className="dt-timer text-[11px] text-muted-foreground">{dtFmt(j.finished_at)}</td>
                    <td className="font-semibold">{j.ano} {j.marca} {j.modelo}</td>
                    <td className="dt-timer text-[11px] text-muted-foreground">{(j.vin || '').slice(-8)}</td>
                    <td className="text-muted-foreground">{j.cor || '—'}</td>
                    <td><span className="ent-tag">{j.tipo_label}</span></td>
                    <td className="text-muted-foreground">{j.worker_name || '—'}</td>
                    <td className="dt-timer text-right font-bold">{short(j.duration_seconds)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Sheet open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <SheetContent side="right" className="w-full border-border bg-card p-0 text-foreground sm:max-w-[420px]" data-testid="dx-drawer">
          {detail && (
            <div className="flex h-full flex-col">
              <div className="border-b border-border p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <span className="ent-tag">{detail.tipo_label}</span>
                    <h2 className="mt-2 font-display text-xl font-black uppercase leading-tight tracking-tight">{detail.ano} {detail.marca} {detail.modelo}</h2>
                    <p className="dt-timer mt-1 text-[11px] tracking-[0.14em] text-muted-foreground/70">VIN {detail.vin}</p>
                  </div>
                  <button onClick={() => setDetail(null)} className="ent-btn ent-btn-icon" data-testid="dx-drawer-close"><X className="h-3.5 w-3.5" /></button>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto">
                <Row label="Duração" value={short(detail.duration_seconds)} />
                <Row label="Profissional" value={detail.worker_name} />
                <Row label="Iniciado em" value={dtFmt(detail.started_at)} />
                <Row label="Finalizado em" value={dtFmt(detail.finished_at)} />
                <Row label="Adicionado por" value={detail.created_by_name} />
                <Row label="Criado em" value={dtFmt(detail.created_at)} />
                <Row label="Trim" value={detail.trim} />
                <Row label="Cor" value={detail.cor} />
                <Row label="Carroceria" value={detail.carroceria} />
                {detail.notas && (
                  <div className="px-3 py-3">
                    <p className="ent-label">Notas</p>
                    <p className="mt-1.5 border-l-2 border-primary/50 pl-3 text-xs text-muted-foreground">{detail.notas}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default ExecutedJobs;
